// iOS Safari reports `HTMLMediaElement.duration === Infinity` for freshly recorded
// blobs because the MP4 fragment's moov atom is at the end. Decoding through Web
// Audio gives us the real duration in seconds.
//
// Falls back to HTMLAudioElement duration if AudioContext decode fails.

export async function decodeBlobDurationSec(blob: Blob): Promise<number> {
  try {
    const AC =
      (window as unknown as { AudioContext?: typeof AudioContext; webkitAudioContext?: typeof AudioContext })
        .AudioContext ??
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AC) throw new Error('no AudioContext');
    const ctx = new AC();
    try {
      const buf = await blob.arrayBuffer();
      const audio = await ctx.decodeAudioData(buf.slice(0));
      return audio.duration;
    } finally {
      void ctx.close();
    }
  } catch {
    return decodeViaMediaElement(blob);
  }
}

function decodeViaMediaElement(blob: Blob): Promise<number> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(blob);
    const a = new Audio();
    const done = (v: number) => {
      URL.revokeObjectURL(url);
      resolve(Number.isFinite(v) && v > 0 ? v : 0);
    };
    a.addEventListener('loadedmetadata', () => {
      if (Number.isFinite(a.duration)) done(a.duration);
      // iOS Infinity workaround: seek past end to force metadata finalize.
      else {
        a.currentTime = 1e9;
        a.addEventListener('timeupdate', function onTu() {
          a.removeEventListener('timeupdate', onTu);
          done(a.duration);
        });
      }
    });
    a.addEventListener('error', () => done(0));
    a.src = url;
  });
}

export function formatMmSs(totalSec: number): string {
  if (!Number.isFinite(totalSec) || totalSec < 0) return '00:00';
  const s = Math.floor(totalSec);
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
}
